import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { ConfirmOptions } from 'components/modals/ConfirmationContext';

export interface ActionFormDirtyState {
  isDirty: boolean;
  formName?: string;
}

interface ActionFormDirtyContextValue {
  setIsDirty: (isDirty: boolean) => void;
  setFormName: (formName?: string) => void;
}

const ActionFormDirtyContext = createContext<ActionFormDirtyContextValue | undefined>(undefined);

/**
 * Creates a dirty-state store for an action dialog. The store is read
 * synchronously by the close handler to decide whether a discard confirmation is
 * needed, while the open form and the dialog header report into it through
 * context. State is held in a ref so the close handler always sees the latest
 * value without re-rendering the dialog tree.
 */
export const useActionFormDirtyStore = () => {
  const stateRef = useRef<ActionFormDirtyState>({ isDirty: false });

  const setIsDirty = useCallback((isDirty: boolean) => {
    stateRef.current = { ...stateRef.current, isDirty };
  }, []);

  const setFormName = useCallback((formName?: string) => {
    stateRef.current = { ...stateRef.current, formName };
  }, []);

  const getDirtyState = useCallback(() => stateRef.current, []);

  const contextValue = useMemo<ActionFormDirtyContextValue>(
    () => ({ setIsDirty, setFormName }),
    [setIsDirty, setFormName]
  );

  return { contextValue, getDirtyState };
};

export const ActionFormDirtyProvider: React.FC<React.PropsWithChildren<{ value: ActionFormDirtyContextValue }>> = ({
  value,
  children,
}) => <ActionFormDirtyContext.Provider value={value}>{children}</ActionFormDirtyContext.Provider>;

/**
 * Reports whether the user has edited the current action form. Forms pass
 * react-hook-form's `formState.isDirty`. Programmatic field updates that don't
 * set `shouldDirty` (rehydration, auto-population) are intentionally not treated
 * as edits, so opening and exploring a form without changing anything will not
 * prompt a discard confirmation on close.
 */
export const useTrackActionFormDirty = (isDirty: boolean) => {
  const context = useContext(ActionFormDirtyContext);

  useEffect(() => {
    context?.setIsDirty(isDirty);
  }, [context, isDirty]);

  useEffect(
    () => () => {
      context?.setIsDirty(false);
    },
    [context]
  );
};

/**
 * Reports the human-readable name of the current action form so the discard
 * confirmation can name what is being closed. Called by the shared dialog header.
 */
export const useReportActionFormName = (formName?: string) => {
  const context = useContext(ActionFormDirtyContext);

  useEffect(() => {
    context?.setFormName(formName);

    return () => {
      context?.setFormName(undefined);
    };
  }, [context, formName]);
};

export const buildDiscardActionFormConfirmation = (formName?: string): ConfirmOptions => ({
  title: 'Discard changes?',
  text: formName ? `Your unsaved changes to ${formName} will be discarded.` : 'Your unsaved changes will be discarded.',
  confirmButtonProps: { title: 'Discard' },
  cancelButtonProps: { title: 'Keep editing' },
});

/**
 * Builds the close handler shared by every action dialog. The dialog closes
 * immediately when the close is forced (e.g. a successful submit) or when the
 * open form reports no edits. Otherwise it asks the user to confirm discarding
 * their unsaved changes, naming the form when known.
 */
export const createActionFormCloseHandler =
  (
    getDirtyState: () => ActionFormDirtyState,
    confirmWithModal: (options: ConfirmOptions) => Promise<boolean>,
    closeDialog: () => void
  ) =>
  async (event?: unknown, force?: boolean): Promise<void> => {
    const { isDirty, formName } = getDirtyState();

    if (force || !isDirty) {
      closeDialog();
      return;
    }

    if (await confirmWithModal(buildDiscardActionFormConfirmation(formName))) {
      closeDialog();
    }
  };
